import { VoiceCallTransferCallMode } from 'flow-schema';

import { api } from 'common';

export const TOOL_DESCRIPTION_LABEL = 'Tool Description (for LLM)';
export const TOOL_DESCRIPTION_HELPER_TEXT =
  'Controls how the LLM decides when to invoke this tool. Leave blank to use the default description.';
export const TOOL_DESCRIPTION_PLACEHOLDER =
  'e.g., Transfer the current call to another destination when the caller requests a human agent, authentication fails, or the request is out of scope.';

export const NUMBER_LABEL = 'Phone number';
export const NUMBER_PLACEHOLDER = '+1-###-###-####';
export const NUMBER_HELPER_TEXT = `The phone number to transfer the call to. US and Canadian numbers are supported.`;

export const SIP_URI_LABEL = 'SIP URI to transfer the call to';
export const SIP_URI_PLACEHOLDER = 'sip:username@host[:port]';
export const SIP_URI_HELPER_TEXT = 'The SIP URI to transfer the call to.';

export const EXTENSION_LABEL = 'Extension (Optional)';
export const EXTENSION_PLACEHOLDER = '1234';
export const EXTENSION_HELPER_TEXT = 'The extension to transfer the call to.';

export const DESTINATION_TYPE_LABEL = 'Transfer destination type';
export const DESTINATION_HELPER_TEXT = `The destination to transfer the call to.`;

export const TRANSFER_MODE_LABEL = 'Transfer mode';
export const TRANSFER_MODE_HELPER_TEXT = `The mode of the transfer call.`;

export const DESCRIPTION_LABEL = 'Transfer destination description';
export const DESCRIPTION_HELPER_TEXT = `The description of the destination the call will be transferred to.`;

export const CUSTOMER_MESSAGE_SWITCH_LABEL = 'Enable Pre-connection Message to Callee (Optional)';
export const CUSTOMER_MESSAGE_LABEL = 'Message to callee (Optional)';
export const CUSTOMER_MESSAGE_HELPER_TEXT = `The message spoken to the callee before connecting them to the destination.`;

export const DESTINATION_MESSAGE_LABEL = 'Message to transfer destination (Optional)';
export const DESTINATION_MESSAGE_HELPER_TEXT = `The message spoken to the transfer destination before connecting the callee.`;

export const SUMMARY_MESSAGES_SYSTEM_PROMPT_LABEL = 'Call summary prompt (Optional)';
export const SUMMARY_MESSAGES_SYSTEM_PROMPT_HELPER_TEXT = `The prompt used to generate the summary of the call using the call transcript.`;

/**
 * Labels specific to the Assistant "Transfer" tab redesign. These intentionally
 * differ from the shared labels above (which the Flow Builder Voice Call block reuses) so the
 * tab can match its Figma design without changing the Flow Builder block's copy.
 */
export const ASSISTANT_INSTRUCTIONS_CARD_TITLE = 'Instructions for Transfer';
export const ASSISTANT_DESCRIPTION_LABEL = 'When to transfer to this destination';
export const ASSISTANT_DESCRIPTION_HELPER_TEXT =
  'The assistant uses this to decide which calls to send here (e.g. "Billing questions"). Keep it unique per destination. Callers don\'t hear it.';
export const ASSISTANT_DESTINATION_TYPE_LABEL = 'Transfer Destination Type';
export const ASSISTANT_NUMBER_LABEL = 'Phone number to transfer call to';
export const ASSISTANT_SIP_URI_LABEL = 'SIP URI to transfer call to';
export const ASSISTANT_EXTENSION_LABEL = 'Extension (optional)';
// The "(Optional)" cue lives on the enable checkbox instead, so the field label stays clean.
export const ASSISTANT_CUSTOMER_MESSAGE_LABEL = 'Message to callee';

interface GetDefaultTransferToolDefinitionOptions {
  organizationId: string;
}

export function getDefaultTransferDestination(): api.Assistant.VoiceCallTransferCallToolDestination {
  return {
    type: api.Assistant.VoiceCallTransferCallToolDestinationType.Number,
    transferMode: api.Assistant.VoiceCallTransferCallMode.BlindTransfer,
    number: '',
    description: '',
    extension: '',
  };
}

export function getDefaultTransferToolDefinition(
  options: GetDefaultTransferToolDefinitionOptions
): api.Assistant.NotableTransferCallToolDefinitionUpsertValues {
  const { organizationId } = options;
  return {
    type: api.AssistantToolDefinition.AssistantToolType.NotableTransferCall,
    name: api.AssistantToolDefinition.AssistantToolType.NotableTransferCall,
    organizationId,
    summary: null,
    description: '',
    metadata: {
      destinations: [getDefaultTransferDestination()],
    },
  };
}

export const TRANSFER_DESTINATION_TYPE_OPTIONS = [
  {
    value: api.Assistant.VoiceCallTransferCallToolDestinationType.Number,
    label: 'Phone Number',
  },
  {
    value: api.Assistant.VoiceCallTransferCallToolDestinationType.Sip,
    label: 'SIP URI',
  },
];

export const VoiceCallTransferCallModeToDisplayNameAndDescription: Record<
  api.Assistant.VoiceCallTransferCallMode,
  { displayName: string; description: string }
> = {
  [api.Assistant.VoiceCallTransferCallMode.BlindTransfer]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.BlindTransfer
    ],
  [api.Assistant.VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader
    ],
  [api.Assistant.VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader
    ],
  [api.Assistant.VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader
    ],
  [api.Assistant.VoiceCallTransferCallMode.WarmTransferSayMessage]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferSayMessage
    ],
  [api.Assistant.VoiceCallTransferCallMode.WarmTransferSaySummary]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferSaySummary
    ],
  [api.Assistant.VoiceCallTransferCallMode
    .WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage
    ],
  [api.Assistant.VoiceCallTransferCallMode
    .WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary
    ],
  [api.Assistant.VoiceCallTransferCallMode.WarmTransferTwiml]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferTwiml
    ],
  [api.Assistant.VoiceCallTransferCallMode.WarmTransferExperimental]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.WarmTransferExperimental
    ],
  [api.Assistant.VoiceCallTransferCallMode.TransferWithDtmfCallToken]:
    api.FlowDefinition.VoiceCallTransferCallModeToDisplayNameAndDescription[
      VoiceCallTransferCallMode.TransferWithDtmfCallToken
    ],
};

export const TRANSFER_MODE_OPTIONS_BY_DESTINATION_TYPE: Record<
  api.Assistant.VoiceCallTransferCallToolDestinationType,
  { label: string; value: api.Assistant.VoiceCallTransferCallMode }[]
> = {
  [api.Assistant.VoiceCallTransferCallToolDestinationType.Number]: [
    {
      value: api.Assistant.VoiceCallTransferCallMode.BlindTransfer,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.BlindTransfer
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.WarmTransferSayMessage,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.WarmTransferSayMessage
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.WarmTransferSaySummary,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.WarmTransferSaySummary
        ].displayName,
    },
    {
      value:
        api.Assistant.VoiceCallTransferCallMode
          .WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode
            .WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage
        ].displayName,
    },
    {
      value:
        api.Assistant.VoiceCallTransferCallMode
          .WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode
            .WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.TransferWithDtmfCallToken,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.TransferWithDtmfCallToken
        ].displayName,
    },
  ],
  [api.Assistant.VoiceCallTransferCallToolDestinationType.Sip]: [
    {
      value: api.Assistant.VoiceCallTransferCallMode.BlindTransfer,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.BlindTransfer
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.BlindTransferWithTokenInSipHeader
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.BlindTransferWithVoiceCallIdInSipHeader
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.BlindTransferAddSummaryToSipHeader
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.WarmTransferSayMessage,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.WarmTransferSayMessage
        ].displayName,
    },
    {
      value: api.Assistant.VoiceCallTransferCallMode.WarmTransferSaySummary,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode.WarmTransferSaySummary
        ].displayName,
    },
    {
      value:
        api.Assistant.VoiceCallTransferCallMode
          .WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode
            .WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage
        ].displayName,
    },
    {
      value:
        api.Assistant.VoiceCallTransferCallMode
          .WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary,
      label:
        VoiceCallTransferCallModeToDisplayNameAndDescription[
          api.Assistant.VoiceCallTransferCallMode
            .WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary
        ].displayName,
    },
  ],
};
